import { addDays, subWeeks } from "date-fns";
import { eqBy, lensProp, prop, propEq, reject, set, unionWith } from "ramda";
import { type ActionTypes, PastFuture, TypeKeys } from "actions/context";
import {
  type WorkforceActionTypes,
  WorkforceTypeKeys,
} from "actions/workforce";
import { AbsoluteDateRange } from "helpers/shared/formatters/dates";

import { type FeatureFlags, type IContext, LoadingStatus } from "types/context";
import { RequestStatus } from "types/request";

const DEFAULT_DATE_RANGE_PAST = {
  start: subWeeks(new Date(), 2),
  end: new Date(),
  rangeKey: AbsoluteDateRange.CUSTOM,
};

const DEFAULT_DATE_RANGE_FUTURE = {
  start: new Date(),
  end: addDays(new Date(), 6),
  rangeKey: AbsoluteDateRange.WEEK,
};

const setDateRange = set(lensProp("dateRange"));
const setDateRangeFuture = set(lensProp("dateRangeFuture"));

const initialState: IContext = {
  appAPIToken: {
    inventory: null,
    inventorySettings: null,
  },
  dateRange: DEFAULT_DATE_RANGE_PAST,
  dateRangeFuture: DEFAULT_DATE_RANGE_FUTURE,
  entities: [],
  featureFlags: {} as FeatureFlags,
  storeKey: null,
  selfRouted: "",
  toolbarSnacks: [],
  userApps: [], // Used only for initial application load
  appLoadingStatus: {
    breadcrumb: LoadingStatus.NOT_LOADED,
    partners: LoadingStatus.NOT_LOADED,
    inventory: LoadingStatus.NOT_LOADED,
  },
  appMessageStatus: {
    breadcrumb: LoadingStatus.NOT_LOADED,
    partners: LoadingStatus.NOT_LOADED,
    inventory: LoadingStatus.NOT_LOADED,
  },
  appAPITokenStatus: {
    inventory: LoadingStatus.NOT_LOADED,
    inventorySettings: LoadingStatus.NOT_LOADED,
  },
  accessRequests: {
    workforce: false,
    workforceRequestStatus: RequestStatus.NOT_STARTED,
  },
};

function context(
  state: IContext = initialState,
  action: ActionTypes | WorkforceActionTypes
): IContext {
  switch (action.type) {
    case TypeKeys.RECEIVE_ENTITIES: {
      return {
        ...state,
        entities: action.entities,
        userApps: action.userApps,
      };
    }

    case TypeKeys.UPDATE_LD_CLIENT_FLAGS: {
      return {
        ...state,
        featureFlags: { ...state.featureFlags, ...action.featureFlags },
      };
    }

    case TypeKeys.UPDATE_DATE_SELECTION: {
      const setter =
        action.pastOrFuture === PastFuture.FUTURE
          ? setDateRangeFuture
          : setDateRange;
      return setter(
        {
          start: action.start,
          end: action.end,
          rangeKey: action.rangeKey,
        },
        state
      );
    }

    case TypeKeys.SET_APP_LOADING_STATUS: {
      return {
        ...state,
        appLoadingStatus: {
          ...state.appLoadingStatus,
          [action.app]: action.status,
        },
      };
    }

    case TypeKeys.SET_APP_MESSAGE_STATUS: {
      return {
        ...state,
        appMessageStatus: {
          ...state.appMessageStatus,
          [action.app]: action.status,
        },
      };
    }

    case TypeKeys.SET_APP_API_TOKEN_STATUS: {
      return {
        ...state,
        appAPITokenStatus: {
          ...state.appAPITokenStatus,
          [action.app]: action.status,
        },
        appAPIToken: {
          ...state.appAPIToken,
          [action.app]: action.token,
        },
      };
    }

    case TypeKeys.SET_SELF_ROUTED_URL: {
      return {
        ...state,
        // Set self-route, prepended with `/`
        selfRouted: action.route.replace(/^([^/])/, "/$1"),
      };
    }

    case TypeKeys.SET_TOOLBAR_SNACK: {
      const { snack } = action;
      if (!snack.message) {
        // Empty messages mean to remove the snack
        return {
          ...state,
          toolbarSnacks: reject(propEq("id", snack.id), state.toolbarSnacks),
        };
      } else {
        return {
          ...state,
          toolbarSnacks: unionWith(
            eqBy(prop("id")),
            [snack],
            state.toolbarSnacks
          ),
        };
      }
    }

    case WorkforceTypeKeys.JOIN_WORKFORCE_PILOT: {
      return {
        ...state,
        accessRequests: {
          ...state.accessRequests,
          workforce: true,
        },
      };
    }

    case WorkforceTypeKeys.CHECK_WORKFORCE_COOKIE: {
      return {
        ...state,
        accessRequests: {
          ...state.accessRequests,
          workforceRequestStatus: action.requestStatus,
          workforce: action.cookieValue,
        },
      };
    }

    default:
      return state;
  }
}

export default context;
